import { useEffect, useState } from "react";
import { Modal, InputSwitch, InputTextCustom, Combobox, ETypeOfNotification, setNotification } from "medere-ui-framework";
import { Button } from "primereact/button";
import { useLocationConfigContext } from "../../context/LocationConfigContext";
import CustomCheckBox from "../../../../components/CustomCheckBox/CustomCheckBox";
import { useDispatch } from "react-redux";
import { Sector, Establishment, defaultSectorForm, SectorForm } from "../../../../interface/interface";
import { getWorkingRepositories, getAllSectorTypes, getAllRepositoryTypes, loadSectorsByBuildingId } from "../../../../servicesHooks/Location/useLocationConfigService";
import { findPreferredEstablishment } from "../../preferredEstablishment";

type SectorFormErrors = { name?: string; shortName?: string; sectorType?: string; externalId?: string; repository?: string; workingRepository?: string; establishment?: string; prescriptionPriority?: string; };

const MAX_PRESCRIPTION_PRIORITY = 999;

function validatePrescriptionPriority(value: string): string | undefined {
    const trimmed = value.trim();
    if (!trimmed) return "Este campo es requerido";
    if (!/^\d+$/.test(trimmed) || Number(trimmed) > MAX_PRESCRIPTION_PRIORITY) return `Ingresá un número entre 0 y ${MAX_PRESCRIPTION_PRIORITY}`;
    return undefined;
}

interface Props {
    visible: boolean;
    setVisible: (v: boolean) => void;
    onSubmit: (sector: Sector, est: number) => void;
    sectorToEdit?: Sector;
    isCreateModeFromEstablishment?: boolean;
    establishmentOptions?: Establishment[];
}

export default function CRUDSectorModal({ visible, setVisible, onSubmit, sectorToEdit, isCreateModeFromEstablishment, establishmentOptions }: Props) {
    const { establishments, preferredBuildingId } = useLocationConfigContext();
    const dispatch = useDispatch();

    const [form, setForm] = useState<SectorForm>(() => initForm());
    const [formErrors, setFormErrors] = useState<SectorFormErrors>({})
    const [selectedEstablishment, setSelectedEstablishment] = useState<Establishment | null>(null);
    const [selectedSectorType, setSelectedSectorType] = useState<{ id: number; description: string } | { description: string } | null>(null);
    const [availableSectors, setAvailableSectors] = useState<Sector[]>([]);
    const [touchedExternalId, setTouchedExternalId] = useState(false);

    useEffect(() => {
        if (sectorToEdit) {
            const est = establishments.find(e => e.buildingid === sectorToEdit.building);
            setSelectedEstablishment(est ?? null);
            setSelectedSectorType({
                id: sectorToEdit.sectortype,
                description: sectorToEdit.sectortypedescription
            });
        } else {
            // Alta: arranca con el establecimiento preferido del usuario, si está entre las opciones.
            setSelectedEstablishment(findPreferredEstablishment(establishmentOptions ?? establishments, preferredBuildingId));
            setSelectedSectorType(null);
        }
        setForm(initForm());
    }, [visible]);

    function initForm(): SectorForm { //Se encarga de precargar el sector a editar o inicializar el sector a crear
        if (!sectorToEdit) {
            return defaultSectorForm
        }
        return {
            id: sectorToEdit.sector,
            name: sectorToEdit.name,
            sectorTypeId: sectorToEdit.sectortype,
            sectorTypeDesc: sectorToEdit.sectortypedescription,
            externalId: sectorToEdit.externalcode,
            repositoryId: sectorToEdit.repositorytype,
            repositoryDesc: sectorToEdit.repositorytypedescription,
            workingRepositoryId: sectorToEdit.parentsector,
            workingRepositoryDesc: sectorToEdit.parentsectorname,
            shortName: sectorToEdit.shortname,
            acceptsTransfers: sectorToEdit.acceptpatienttransfers,
            restrictRoomGenderControl: sectorToEdit.restrictroomgendercontrol ?? true,
            prescriptionPriority: sectorToEdit.prescriptionpriority != null ? String(sectorToEdit.prescriptionpriority) : '',
            active: sectorToEdit.active,
            establishmentId: sectorToEdit.building
        };
    }

    const handleSubmit = async () => {
        const errors = validateForm(form);
        setFormErrors(errors);
        const hasErrors = Object.keys(errors).length > 0;

        if (hasErrors || !selectedEstablishment) return;

        const newSector: Sector = {
            sector: form.id,
            name: form.name,
            sectortype: form.sectorTypeId,
            sectortypedescription: form.sectorTypeDesc,
            externalcode: form.externalId,
            repositorytype: form.repositoryId,
            repositorytypedescription: form.repositoryDesc,
            parentsector: form.workingRepositoryId > 0 ? form.workingRepositoryId : null,
            parentsectorname: form.workingRepositoryId > 0 ? form.workingRepositoryDesc : "",
            shortname: form.shortName,
            acceptpatienttransfers: form.acceptsTransfers,
            restrictroomgendercontrol: form.restrictRoomGenderControl,
            prescriptionpriority: Number(form.prescriptionPriority.trim()),
            active: form.active,
            building: selectedEstablishment.buildingid,
            buildigname: selectedEstablishment.buildingname
        };

        try {
            await onSubmit(newSector, selectedEstablishment.buildingid);
            dispatch(setNotification({
                title: sectorToEdit && sectorToEdit.sector > 0 ? "Edición exitosa" : "Creación exitosa",
                desc: sectorToEdit && sectorToEdit.sector > 0 ? "Se ha editado el sector" : "Se ha creado el sector",
                icon: "pi-check-circle",
                typeOfNotification: ETypeOfNotification.INFO
            }));
        } catch (error) {
            console.error(error);
            dispatch(setNotification({
                title: "Error",
                desc: "No se pudo guardar el sector",
                icon: "pi-times",
                typeOfNotification: ETypeOfNotification.ERROR
            }));
        }
    };

    const validateForm = (form: SectorForm): SectorFormErrors => {

        const errors: SectorFormErrors = {};
        if (!form.name.trim()) errors.name = "Este campo es requerido";
        if (!form.shortName.trim()) errors.shortName = "Este campo es requerido";
        if (form.sectorTypeId <= 0 || !form.sectorTypeDesc.trim()) errors.sectorType = "Este campo es requerido";
        if (form.externalId.trim()) {
            const code = form.externalId.trim().toUpperCase();
            const takenCodes = availableSectors.filter(s => s.building === selectedEstablishment?.buildingid).map(s => s.externalcode?.toUpperCase());
            const isEditing = !!sectorToEdit;
            const sameAsCurrent = isEditing && code === sectorToEdit!.externalcode?.toUpperCase();
            if (takenCodes.includes(code) && !sameAsCurrent) {
                errors.externalId = "Ya existe un sector con ese código externo";
            }
        }
        if (form.repositoryId <= 0 || !form.repositoryDesc.trim()) errors.repository = "Este campo es requerido";
        if (form.workingRepositoryId && form.workingRepositoryId > 0 && !form.workingRepositoryDesc.trim()) {
            errors.workingRepository = "Descripción inválida";
        }
        if (!selectedEstablishment || selectedEstablishment.buildingid <= 0) errors.establishment = "Este campo es requerido";
        const priorityError = validatePrescriptionPriority(form.prescriptionPriority);
        if (priorityError) errors.prescriptionPriority = priorityError;

        if (form.shortName.trim()) {
            const sn = form.shortName.trim().toUpperCase();
            const taken = availableSectors.filter(s => s.building === selectedEstablishment?.buildingid).map(s => s.shortname.toUpperCase());
            const isEditing = !!sectorToEdit;
            const sameAsCurrent = isEditing && sn === sectorToEdit!.shortname.toUpperCase();
            if (taken.includes(sn) && !sameAsCurrent) {
                errors.shortName = "Ya existe un sector con esa abreviatura";
            }
        }

        return errors;
    };
    const [sectorTypeOptions, setSectorTypeOptions] = useState<{ id: number; description: string }[]>([]);
    const [repositoryTypeOptions, setRepositoryTypeOptions] = useState<{ id: number; description: string }[]>([]);
    const [workingRepoOptions, setWorkingRepoOptions] = useState<{ id: number; description: string }[]>([]);

    useEffect(() => {
        if (!selectedEstablishment) return;
        setForm({
            ...form,
            workingRepositoryId: 0,
            workingRepositoryDesc: ''
        });
    }, [selectedEstablishment]);

    useEffect(() => {
        if (!selectedEstablishment) {
            setSectorTypeOptions([]);
            setRepositoryTypeOptions([]);
            setWorkingRepoOptions([]);
            return;
        }

        getAllSectorTypes().then(setSectorTypeOptions);
        getAllRepositoryTypes().then(setRepositoryTypeOptions);

        const repoId = form.repositoryId > 0 ? form.repositoryId : 0;
        getWorkingRepositories(selectedEstablishment.buildingid, repoId).then(setWorkingRepoOptions);
    }, [selectedEstablishment, form.repositoryId]);

    useEffect(() => {
        if (selectedEstablishment) {
            loadSectorsByBuildingId({ buildingid: selectedEstablishment.buildingid })
                .then(res => setAvailableSectors(res ?? []));
        } else {
            setAvailableSectors([]);
        }
    }, [selectedEstablishment]);

    useEffect(() => {
        if (selectedEstablishment && form.repositoryId > 0) {
            getWorkingRepositories(selectedEstablishment.buildingid, form.repositoryId).then(setWorkingRepoOptions);
        } else {
            setWorkingRepoOptions([]);
        }
    }, [form.repositoryId, selectedEstablishment]);

    useEffect(() => {
        if (!touchedExternalId) return;

        const id = form.externalId.trim().toUpperCase();
        if (!id) {
            setFormErrors(prev => ({ ...prev, externalId: "Este campo es requerido" }));
            return;
        }

        const taken = availableSectors
            .filter(s => s.building === selectedEstablishment?.buildingid)
            .map(s => s.externalcode?.toUpperCase());

        const isEditing = !!sectorToEdit;
        const sameAsCurrent = isEditing && id === sectorToEdit!.externalcode?.toUpperCase();

        if (taken.includes(id) && !sameAsCurrent) {
            setFormErrors(prev => ({ ...prev, externalId: "Ya existe un sector con ese código" }));
        } else {
            setFormErrors(prev => ({ ...prev, externalId: "" }));
        }

    }, [selectedEstablishment, form.externalId, availableSectors, touchedExternalId]);

    const availableEstablishments = establishmentOptions ?? establishments;

    return (
        <Modal className="location-mobile-editor-modal" visible={visible} setVisible={setVisible} header={<div style={{ fontSize: '20px' }}>{isCreateModeFromEstablishment ? "Crear Sector" : "Editar Sector"}</div>}>
            <div className="location-config-view location-sector-modal">
                <div className="justify-self-end flex flex-row align-items-center gap-2 location-switch-inline-row location-modal-active-row">
                    <InputSwitch id="activeInputSwitchId" checked={form.active} onChange={(e) => setForm({ ...form, active: e.value })} />
                    <span className="text-sm location-switch-state-label">{form.active ? "Activo" : "Inactivo"}</span>
                </div>

                <div className="flex gap-2">
                    <div className="w-full">
                        <p>Establecimiento *</p>
                        <Combobox
                            key={"comboSectorId"}
                            className="location-sector-combobox"
                            getItems={() => Promise.resolve(availableEstablishments)}
                            value={selectedEstablishment}
                            setValue={(e) => {
                                setSelectedEstablishment(e)
                                setFormErrors({ ...formErrors, establishment: "" })
                            }}
                            optionLabel="buildingname"
                            placeholder="Seleccionar"
                            error={!!formErrors.establishment}
                            caption={formErrors.establishment}
                        />
                    </div>
                    <div className="w-full">
                        <p>Tipo de sector *</p>
                        <Combobox
                            key={"comboSectorTypeId"}
                            className="location-sector-combobox"
                            list={sectorTypeOptions}
                            value={selectedSectorType}
                            setValue={(e: any) => {
                                setSelectedSectorType(e ?? null);
                                if (typeof e?.id === "number") {
                                    setForm({
                                        ...form,
                                        sectorTypeId: e.id,
                                        sectorTypeDesc: e.description ?? ""
                                    });
                                } else if (!e?.description) {
                                    setForm({
                                        ...form,
                                        sectorTypeId: 0,
                                        sectorTypeDesc: ""
                                    });
                                }
                                setFormErrors({ ...formErrors, sectorType: "" });
                            }}
                            optionLabel="description"
                            placeholder="Seleccionar"
                            error={!!formErrors.sectorType}
                            caption={formErrors.sectorType}
                        />
                    </div>
                </div>

                <div className="flex gap-2">
                    <div className="w-full">
                        <p>Nombre *</p>
                        <InputTextCustom key={"inputSectorNameId"} className={formErrors.name ? "" : "location-sector-input"} value={form.name} onChange={(v) => { setForm({ ...form, name: v.target.value });; setFormErrors({ ...formErrors, name: "" }) }} error={!!formErrors.name} caption={formErrors.name} />
                    </div>
                    <div className="flex w-full gap-2">
                        <div className="w-full">
                            <p>Nombre abreviado *</p>
                            <InputTextCustom key={"inputSectorShortNameId"} className={formErrors.shortName ? "" : "location-sector-input"} value={form.shortName} onChange={(v) => { setForm({ ...form, shortName: v.target.value });; setFormErrors({ ...formErrors, shortName: "" }) }} error={!!formErrors.shortName} caption={formErrors.shortName} />
                        </div>
                        <div className="w-full">
                            <p>Código externo</p>
                            <InputTextCustom
                                key={"inputSectorExternalCodeId"}
                                className={formErrors.externalId ? "" : "location-sector-input"}
                                value={form.externalId}
                                onChange={(v) => {
                                    setTouchedExternalId(true);
                                    const value = v.target.value;
                                    const id = value.trim().toUpperCase();
                                    let error = "";

                                    if (!id) {
                                        error = "Este campo es requerido";
                                    } else {
                                        const taken = availableSectors
                                            .filter(s => s.building === selectedEstablishment?.buildingid)
                                            .map(s => s.externalcode?.toUpperCase());

                                        const isEditing = !!sectorToEdit;
                                        const sameAsCurrent = isEditing && id === sectorToEdit!.externalcode?.toUpperCase();

                                        if (taken.includes(id) && !sameAsCurrent) {
                                            error = "Ya existe un sector con ese código externo";
                                        }
                                    }

                                    setForm({ ...form, externalId: value });
                                    setFormErrors({ ...formErrors, externalId: error });
                                }}
                                error={!!formErrors.externalId}
                                caption={formErrors.externalId}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex gap-2">
                    <div className="w-full">
                        <p>Tipo de repositorio *</p>
                        <Combobox
                            key={"comboRepositoryTypeId"}
                            className="location-sector-combobox"
                            list={repositoryTypeOptions}
                            value={form.repositoryId > 0 || form.repositoryDesc ? { id: form.repositoryId, description: form.repositoryDesc } : null}
                            setValue={(e: any) => {
                                setForm({
                                    ...form,
                                    repositoryId: typeof e?.id === "number" ? e.id : 0,
                                    repositoryDesc: e?.description ?? "",
                                    workingRepositoryId: 0,
                                    workingRepositoryDesc: ""
                                });
                                setFormErrors({ ...formErrors, repository: "", workingRepository: "" });
                            }}
                            optionLabel="description"
                            placeholder="Seleccionar"
                            error={!!formErrors.repository}
                            caption={formErrors.repository}
                        />
                    </div>
                    <div className="w-full">
                        <p>Repositorio de trabajo</p>
                        <Combobox
                            key={"comboWorkingRepositoryTypeId"}
                            className="location-sector-combobox"
                            list={availableSectors.map(s => ({ id: s.sector, description: s.name }))}
                            autoselectOnOneItem={false}
                            value={ form.workingRepositoryId > 0 ? { id: form.workingRepositoryId, description: form.workingRepositoryDesc } : null }
                            setValue={(e: any) => {
                                if (e && e.id !== 0) {
                                    setForm({
                                        ...form,
                                        workingRepositoryId: e.id,
                                        workingRepositoryDesc: e.description
                                    });
                                    setFormErrors({ ...formErrors, workingRepository: "" });
                                }
                            }}
                            optionLabel="description"
                            placeholder="Seleccionar"
                            error={!!formErrors.workingRepository}
                            caption={formErrors.workingRepository}
                        />
                    </div>
                </div>

                <div className="flex gap-2">
                    <div className="w-full">
                        <p>Prioridad para prescripciones *</p>
                        <InputTextCustom
                            key={"inputSectorPrescriptionPriorityId"}
                            className={formErrors.prescriptionPriority ? "" : "location-sector-input"}
                            keyfilter="pint"
                            placeholder={`0 a ${MAX_PRESCRIPTION_PRIORITY}`}
                            value={form.prescriptionPriority}
                            onChange={(v) => { setForm({ ...form, prescriptionPriority: v.target.value }); setFormErrors({ ...formErrors, prescriptionPriority: "" }) }}
                            error={!!formErrors.prescriptionPriority}
                            caption={formErrors.prescriptionPriority || `0 es la máxima prioridad y ${MAX_PRESCRIPTION_PRIORITY} la menor`}
                        />
                    </div>
                    <div className="w-full" />
                </div>

                <div className="flex flex-col">
                    <label className="mb-2.5">
                        <CustomCheckBox
                            key={"checkBoxTransferId"}
                            checked={form.acceptsTransfers}
                            onChange={(e) => setForm({ ...form, acceptsTransfers: e.target.checked })}
                        />
                        <span style={{ fontSize: '15px' }}>Traslada pacientes</span>
                    </label>
                    <label className="mb-2.5">
                        <CustomCheckBox
                            key={"checkBoxRoomGenderControlId"}
                            checked={form.restrictRoomGenderControl}
                            onChange={(e) => setForm({ ...form, restrictRoomGenderControl: e.target.checked })}
                        />
                        <span style={{fontSize: '15px'}}>Restringir pacientes de distinto sexo en la misma habitación</span>
                    </label>
                </div>

                <div className="flex justify-content-end align-items-center mt-6 gap-2">
                    <Button id="cancelButtonId" label="Cancelar" onClick={() => setVisible(false)} className="btnCancel2 btnWidth" />
                    <Button id="createButtonId" label={sectorToEdit ? "Guardar" : "Crear"} className="btnContinue btnWidth" onClick={handleSubmit} />
                </div>
            </div>
        </Modal>
    );
}

